import { AttackPattern, PatternState } from '../AttackPattern.js';

/**
 * Temporal Rewind (The Chronophage) — fires a few lazy, wide-spaced rings…
 * then time hiccups and EVERY bullet in the boss's pool reverses course
 * (slightly faster than it left), sweeping back across the arena toward
 * where it came from. The dodge you already made must be re-made in
 * reverse, and any other layered pattern's bullets get caught in the
 * rewind too — deliberately: the whole fight obeys the same clock.
 *
 * The reversal beat is telegraphed by a distinct shrinking-clock visual
 * for 0.6s before it lands, so the second pass is always a read, never a
 * sucker punch.
 */
export class TemporalRewind extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Temporal Rewind', telegraphDuration: 1.0, activeDuration: 4.6, minPhase });
    this.config = config;
  }

  onBegin() {
    this._elapsed = 0;
    this._ringsFired = 0;
    this._ringTimer = 0;
    this._rewound = false;
    this._baseAngle = Math.random() * Math.PI * 2;
  }

  onActiveTick(dt, boss, _player, pool) {
    this._elapsed += dt;

    this._ringTimer -= dt;
    if (this._ringsFired < 3 && this._ringTimer <= 0) {
      this._ringTimer = 0.5;
      const count = this.config.ringCount + boss.phase * 3;
      const rotOffset = this._ringsFired * 0.21;
      for (let i = 0; i < count; i++) {
        const angle = this._baseAngle + rotOffset + (i / count) * Math.PI * 2;
        pool.fire({
          x: boss.x,
          y: boss.y,
          vx: Math.cos(angle) * this.config.ringSpeed,
          vy: Math.sin(angle) * this.config.ringSpeed,
          radius: 2.5,
          damage: this.config.ringDamage,
          color: '#8c7bff',
          lifetime: 5.2,
        });
      }
      this._ringsFired++;
    }

    if (!this._rewound && this._elapsed >= 2.6) {
      this._rewound = true;
      pool.forEachActive((bullet) => {
        bullet.vx *= -1.06;
        bullet.vy *= -1.06;
        // Keep the cached scalar in sync for bullets that steer via
        // homing/accel (both recompute velocity from `speed`).
        bullet.speed = Math.hypot(bullet.vx, bullet.vy);
      });
    }
  }

  getVisual(boss) {
    if (this.state === PatternState.TELEGRAPH) {
      return { kind: 'rewindPulse', x: boss.x, y: boss.y, t: 1 - this._timer / this.telegraphDuration, imminent: false };
    }
    // The rewind's own warning window — distinct from the opening telegraph.
    if (this.state === PatternState.ACTIVE && !this._rewound && this._elapsed >= 2.0) {
      return { kind: 'rewindPulse', x: boss.x, y: boss.y, t: (this._elapsed - 2.0) / 0.6, imminent: true };
    }
    return null;
  }
}
